<!-- LAYOUT: app.vue -->

<script setup lang="ts">
//
// Imports
//

// Vuetify
import { useDisplay } from "vuetify/lib/composables/display.mjs";

// Components
import AppBar from "../components/common/app-bar/template/AppBar.vue";
import Drawer from "../components/common/navigation-drawer/template/Drawer.vue";
import GlobalSnackbar from "../components/shared/GlobalSnackbar.vue";

//
// Composables
//

// Breakpoints
const { smAndDown } = useDisplay();

// Localization
const { t, locale } = useI18n();

// Runtime config (used for reverse-proxy-aware favicon fallback)
const config = useRuntimeConfig();

// Auth
const { userName, userEmail } = useAuth();

// Page Title
const { pageTitle } = useAppBar();

// Laboratory branding - centralized management
const branding = useLaboratoryBranding();
const { laboratoryName, faviconUrl, initialize } = branding;
const labName = computed(() => laboratoryName.value || "");

//
// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================
//

/**
 * Initialize branding on mount
 *
 * Note: useLaboratoryBranding provides:
 * - Fetching logo and name if missing
 * - State management via useState
 * - Blob URL lifecycle management
 * - Cleanup on unmount
 */
onMounted(async () => {
  await initialize();
});

// Watch for locale changes and refresh laboratory name
// Single watcher at layout level - prevents duplicate API calls
watch(locale, async (newLocale) => {
  await branding.refreshName(newLocale);
});

//
// Watchers
//

// Update document head when page title or laboratory data changes
watch(
  [pageTitle, faviconUrl, labName],
  () => {
    const appTitle =
      labName.value || t("components.common.navigation-drawer.drawer_header.app_name");
    useHead({
      title: `${pageTitle.value} - ${appTitle}`,

      htmlAttrs: {
        lang: locale.value,
      },

      link: [
        {
          rel: "icon",
          type: faviconUrl.value ? "image/png" : "image/x-icon",
          href: faviconUrl.value || pathCombine(config.public.APP_BASE_URL, "/favicon.ico"),
          key: "favicon",
        },
      ],

      meta: [
        {
          name: "author",
          content: "SKYWARE Group",
        },
        { name: "robots", content: "index, follow" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },

        // Open Graph
        { property: "og:title", content: pageTitle.value },
        { property: "og:type", content: "website" },
      ],
    });
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <div>
    <!-- Navigation Drawer -->
    <drawer
      :app-name="t('components.common.navigation-drawer.drawer_header.app_name')"
      :user-name="userName"
      :user-email="userEmail"
    />

    <!-- App Bar -->
    <app-bar :show-page-title="!smAndDown" />

    <v-main>
      <v-container fluid class="pa-0 mb-16">
        <slot />
      </v-container>
    </v-main>

    <!-- Snackbar -->
    <global-snackbar />
  </div>
</template>
